import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Poll the hub for the notification log. Refresh interval defaults to 15s —
 * the alerting tick on the hub also runs every 15s, so two ticks per minute
 * is plenty to keep the Logs page fresh without hammering.
 *
 * Returns `refresh()` so the SettingsPage's "test notification" button can
 * force-refresh the log immediately after dispatch.
 */
export function useNotifications({ intervalSeconds = 15, limit = 200 } = {}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOnce = useCallback(
    async (signal) => {
      try {
        const data = await api.listNotifications(limit);
        if (signal?.aborted) return;
        setLogs(data.notifications);
        setLoading(false);
        setError(null);
      } catch (e) {
        if (signal?.aborted) return;
        setError(e);
        setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchOnce(ctrl.signal);
    const id = setInterval(() => fetchOnce(ctrl.signal), Math.max(1, intervalSeconds) * 1000);
    return () => {
      ctrl.abort();
      clearInterval(id);
    };
  }, [fetchOnce, intervalSeconds]);

  return { logs, loading, error, refresh: () => fetchOnce() };
}
